import {EstadoSaludSchema, type EstadoSalud} from '@cnc/contracts';
import {useQuery} from '@tanstack/react-query';
import {ErrorDeApi, pedir} from '../api/cliente';
import {tr, trx, txt, type Texto} from '../i18n/idioma';
import {fechaHora} from '../utilidades/formato';
import {AYUDA_ESTADO as E} from '../componentes/ayuda-general';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';

const NOMBRE_MOTOR: Record<EstadoSalud['baseDeDatos']['motor'], Texto | string> = {
  pglite: txt('PostgreSQL embebido (local)', 'Embedded PostgreSQL (local)'),
  postgres: 'PostgreSQL',
};

export function PaginaEstado() {
  const consulta = useQuery({
    queryKey: ['salud'],
    queryFn: () => pedir('salud', EstadoSaludSchema),
    refetchInterval: 15_000,
    retry: 1,
  });

  return (
    <section className="max-w-4xl">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold text-verde-oscuro">{tr('Estado del sistema', 'System status')}</h1>
        <button
          type="button"
          onClick={() => void consulta.refetch()}
          className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel"
        >
          {tr('↻ Actualizar', '↻ Refresh')}
        </button>
      </div>

      {consulta.isPending && <p className="mt-6 text-apagado">{tr('Consultando el estado…', 'Checking status…')}</p>}

      {consulta.isError && (
        <div className="mt-6 rounded-lg border border-alerta/30 bg-red-50 p-4 text-sm">
          <div className="font-medium text-alerta">
            {tr('No se pudo consultar el estado', 'Could not check the status')}
          </div>
          <div className="mt-1">{consulta.error.message}</div>
          {consulta.error instanceof ErrorDeApi && consulta.error.id && (
            <div className="mt-2 text-xs text-apagado">
              {tr('Referencia:', 'Reference:')} {consulta.error.id}
            </div>
          )}
        </div>
      )}

      {consulta.data && <Tarjetas estado={consulta.data} />}
    </section>
  );
}

function Tarjetas({estado}: {estado: EstadoSalud}) {
  const bd = estado.baseDeDatos;
  const al = estado.almacenamiento;
  return (
    <div className="mt-6 grid gap-4 sm:grid-cols-2">
      <Tarjeta titulo={tr('Estado general', 'Overall status')} bien={estado.estado === 'ok'}>
        <Dato
          nombre={tr('Estado', 'Status')}
          ayuda={E.estado}
          valor={estado.estado === 'ok' ? tr('Todo en orden', 'All good') : tr('Con problemas', 'Has problems')}
        />
        <Dato nombre={tr('Versión', 'Version')} valor={estado.version} ayuda={E.version} />
        <Dato
          nombre={tr('Entorno', 'Environment')}
          ayuda={E.entorno}
          valor={estado.entorno === 'production' ? tr('Producción', 'Production') : tr('Desarrollo', 'Development')}
        />
        <Dato
          nombre={tr('Tiempo activo', 'Uptime')}
          valor={tiempoLegible(estado.tiempoActivoSegundos)}
          ayuda={E.tiempoActivo}
        />
      </Tarjeta>
      <Tarjeta titulo={tr('Base de datos', 'Database')} bien={bd.conectada && bd.migracionesPendientes === 0}>
        <Dato nombre={tr('Motor', 'Engine')} valor={trx(NOMBRE_MOTOR[bd.motor])} ayuda={E.motor} />
        <Dato
          nombre={tr('Conexión', 'Connection')}
          ayuda={E.conexion}
          valor={bd.conectada ? tr('Conectada', 'Connected') : tr('Sin conexión', 'Not connected')}
        />
        <Dato
          nombre={tr('Migraciones', 'Migrations')}
          ayuda={E.migraciones}
          valor={tr(
            `${bd.migracionesAplicadas} aplicadas, ${bd.migracionesPendientes} pendientes`,
            `${bd.migracionesAplicadas} applied, ${bd.migracionesPendientes} pending`,
          )}
        />
        {bd.detalle && <Dato nombre={tr('Ubicación', 'Location')} valor={bd.detalle} pequeno ayuda={E.ubicacionBase} />}
      </Tarjeta>
      <Tarjeta titulo={tr('Archivos', 'Files')} bien={al.disponible}>
        <Dato
          nombre={tr('Tipo', 'Type')}
          valor={al.tipo === 'local' ? tr('Carpeta local', 'Local folder') : al.tipo}
          ayuda={E.tipoArchivos}
        />
        <Dato
          nombre={tr('Disponible', 'Available')}
          valor={al.disponible ? tr('Sí', 'Yes') : tr('No', 'No')}
          ayuda={E.disponible}
        />
        {al.detalle && (
          <Dato nombre={tr('Ubicación', 'Location')} valor={al.detalle} pequeno ayuda={E.ubicacionArchivos} />
        )}
      </Tarjeta>
      <Tarjeta titulo={tr('Última consulta', 'Last check')} bien>
        <Dato nombre={tr('Fecha', 'Date')} valor={fechaHora(estado.fecha)} ayuda={E.fecha} />
      </Tarjeta>
    </div>
  );
}

function Tarjeta({titulo, bien, children}: {titulo: string; bien: boolean; children: React.ReactNode}) {
  return (
    <div className="rounded-lg border border-linea bg-white p-5">
      <div className="flex items-center justify-between">
        <h2 className="font-medium">{titulo}</h2>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${bien ? 'bg-verde-claro text-verde-oscuro' : 'bg-dorado-claro text-tinta'}`}
        >
          {bien ? tr('Bien', 'OK') : tr('Revisar', 'Check')}
        </span>
      </div>
      <dl className="mt-3 space-y-2">{children}</dl>
    </div>
  );
}

function Dato({
  nombre,
  valor,
  pequeno = false,
  ayuda,
}: {
  nombre: string;
  valor: string;
  pequeno?: boolean;
  ayuda?: Texto;
}) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <dt className="text-apagado">
        {nombre}
        {ayuda && <NotaDeAyuda titulo={nombre} texto={ayuda} />}
      </dt>
      <dd className={`text-right ${pequeno ? 'break-all text-xs text-apagado' : ''}`}>{valor}</dd>
    </div>
  );
}

function tiempoLegible(segundos: number) {
  if (segundos < 60) return `${segundos} s`;
  if (segundos < 3600) return `${Math.floor(segundos / 60)} min`;
  const horas = Math.floor(segundos / 3600);
  return `${horas} h ${Math.floor((segundos % 3600) / 60)} min`;
}
